import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  Banner,
  ConfirmDialog,
  Icon,
  PageFrame,
  PageHeader,
  Sheet,
  StatusChip,
  WS_COLOR,
  WS_CONTROL,
  WS_RADIUS,
  WorkspaceThemeProvider,
  icons,
  tableSx,
} from '@platform/ui-kit/workspace';
import {
  api,
  type AirFitAnalysis,
  type AirFitSummaryGroup,
  type AirFitSummaryOpportunity,
} from '../../../api';

/**
 * Strategic Intelligence → Platform Fit Summary, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `PlatformFitSummary.tsx`, which
 * stays the default until the owner approves this one (CLAUDE.md 1.2).
 *
 * WHAT IS UNCHANGED: the API calls (summary, export status, export, assign), the filter fields,
 * the selection rules (only analysed initiatives can be ticked), the "N selected · X of Y
 * analyzed" count, Generate / Download Word and PPT and when the label flips, the first-14-words
 * preview and "more", the six-section analysis in the source's order and wording, Assign and
 * Unassign, the justification required to change or clear an existing assignment, and every
 * message. An ASSIGNED platform is still marked as a decision a person made.
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame (locked header and
 * toolbar, one scroll region), projects as disclosure buttons (the original's were clickable
 * divs), the table in the one table style, the browser `prompt()` as a dialog with a field, the
 * 560px slide-over as the standard sheet, scores as plain figures, and ASSIGNED as a chip.
 */

export function PlatformFitSummaryWorkspace() {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen />
    </WorkspaceThemeProvider>
  );
}

/** A table that wraps its text: the registry style is single-line, this one holds prose. */
const PROSE_TABLE_SX = {
  ...tableSx(WS_CONTROL.rowHeight, false),
  '& td': {
    padding: '10px 12px',
    borderBottom: `1px solid ${WS_COLOR.border}`,
    whiteSpace: 'normal',
    verticalAlign: 'top',
  },
  '& th': {
    ...tableSx(WS_CONTROL.rowHeight, false)['& th'],
    position: 'static',
    whiteSpace: 'normal',
  },
} as const;

function Screen() {
  const [groups, setGroups] = useState<AirFitSummaryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [panel, setPanel] = useState<AirFitSummaryOpportunity | null>(null);
  const [exporting, setExporting] = useState<'word' | 'ppt' | null>(null);
  /* Changing or clearing an existing assignment asks why first. */
  const [asking, setAsking] = useState<{
    opp: AirFitSummaryOpportunity;
    platformId: number | null;
  } | null>(null);
  const [why, setWhy] = useState('');
  const [exportStatus, setExportStatus] = useState<{
    analyzed: number;
    covered: number;
    word: { filename: string; size: number } | null;
    ppt: { filename: string; size: number } | null;
  } | null>(null);

  const runExport = async (format: 'word' | 'ppt') => {
    if (!selected.size) return;
    setExporting(format);
    setError(null);
    try {
      const { blob, filename } = await api.aiReadiness.exportFitSummary([...selected], format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const d = await api.aiReadiness.getFitExportStatus([...selected]);
      setExportStatus({ analyzed: d.analyzed, covered: d.covered, word: d.word, ppt: d.ppt });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setExporting(null);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.aiReadiness
      .getPlatformFitSummary()
      .then((d) => {
        setGroups(d.projectGroups ?? []);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  /* The labels depend on whether a document exists for EXACTLY this selection. */
  useEffect(() => {
    if (!selected.size) {
      setExportStatus(null);
      return;
    }
    api.aiReadiness
      .getFitExportStatus([...selected])
      .then((d) =>
        setExportStatus({ analyzed: d.analyzed, covered: d.covered, word: d.word, ppt: d.ppt }),
      )
      .catch(() => setExportStatus(null));
  }, [selected]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        opps: g.opps.filter(
          (o) =>
            o.opportunity_id.toLowerCase().includes(q) ||
            o.description.toLowerCase().includes(q) ||
            (o.bestFit ?? '').toLowerCase().includes(q) ||
            g.project_id.toLowerCase().includes(q) ||
            g.description.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.opps.length);
  }, [groups, filter]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const doAssign = (
    o: AirFitSummaryOpportunity,
    platformId: number | null,
    justification: string,
  ) => {
    setError(null);
    api.aiReadiness
      .assignPlatform({ opportunity_id: o.opportunity_id, platform_id: platformId, justification })
      .then((r) => {
        setNotice(
          r.unchanged
            ? 'No change — that platform is already assigned.'
            : r.assigned
              ? 'Platform assigned to the opportunity.'
              : 'Platform assignment cleared.',
        );
        load();
      })
      .catch((e: Error) => setError(e.message));
  };

  const assign = (o: AirFitSummaryOpportunity, platformId: number | null) => {
    if (o.assigned) {
      setWhy('');
      setAsking({ opp: o, platformId });
      return;
    }
    doAssign(o, platformId, '');
  };

  const submitWhy = () => {
    if (!asking) return;
    const justification = why.trim();
    const target = asking;
    setAsking(null);
    if (!justification) {
      setError('A justification is required to change or clear the assigned platform.');
      return;
    }
    doAssign(target.opp, target.platformId, justification);
  };

  const totalOpps = groups.reduce((n, g) => n + g.opps.length, 0);
  const analysedOpps = groups.reduce((n, g) => n + g.opps.filter((o) => o.analysis).length, 0);

  /* The first 14 words and a "more" link, as the original. */
  const words = (t: string | null) =>
    String(t ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  const preview = (t: string | null) => {
    const w = words(t);
    return w.slice(0, 14).join(' ') + (w.length > 14 ? '…' : '');
  };

  const toggleOpen = (pid: string) =>
    setExpanded((cur) => {
      const n = new Set(cur);
      if (n.has(pid)) n.delete(pid);
      else n.add(pid);
      return n;
    });

  return (
    <>
      <PageFrame
        header={<PageHeader title="Platform Fit Summary" />}
        toolbar={
          <>
            <Box
              role="search"
              sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', py: 3 }}
            >
              <TextField
                placeholder="Filter by project or opportunity…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                inputProps={{ 'aria-label': 'Filter by project or opportunity' }}
                sx={{ flex: '0 1 320px', minWidth: 200 }}
              />
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" role="status" sx={{ color: WS_COLOR.textMuted }}>
                {selected.size} selected · {analysedOpps} of {totalOpps} analyzed
              </Typography>
              <Button
                variant="contained"
                disabled={!selected.size || exporting !== null}
                title={!selected.size ? 'Select at least one analyzed initiative' : undefined}
                onClick={() => void runExport('word')}
                startIcon={<Icon icon={icons.Download} size={16} />}
              >
                {exporting === 'word'
                  ? 'Generating…'
                  : exportStatus?.word
                    ? 'Download Word'
                    : 'Generate Word'}
              </Button>
              <Button
                variant="tonal"
                disabled={!selected.size || exporting !== null}
                title={!selected.size ? 'Select at least one analyzed initiative' : undefined}
                onClick={() => void runExport('ppt')}
                startIcon={<Icon icon={icons.Download} size={16} />}
              >
                {exporting === 'ppt'
                  ? 'Generating…'
                  : exportStatus?.ppt
                    ? 'Download PPT'
                    : 'Generate PPT'}
              </Button>
            </Box>
            {error || notice ? (
              <Box sx={{ display: 'grid', gap: 2, pb: 3 }}>
                {error ? <Banner tone="error">{error}</Banner> : null}
                {notice ? (
                  <Banner tone="success" onDismiss={() => setNotice(null)}>
                    {notice}
                  </Banner>
                ) : null}
              </Box>
            ) : null}
          </>
        }
      >
        <Box tabIndex={0} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', pb: 4 }}>
          {loading ? (
            <Typography role="status" sx={{ color: WS_COLOR.textMuted }}>
              Loading…
            </Typography>
          ) : !visible.length ? (
            <Typography sx={{ color: WS_COLOR.textMuted }}>
              {groups.length
                ? 'No project or opportunity matches that filter.'
                : 'No analyzed opportunities yet. Run an AI Platform Fitness Analysis first.'}
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 3 }}>
              {visible.map((g) => {
                const analysed = g.opps.filter((o) => o.analysis).length;
                const all = g.opps.every((o) => selected.has(o.opportunity_id));
                const some = !all && g.opps.some((o) => selected.has(o.opportunity_id));
                const open = expanded.has(g.project_id);
                const bodyId = `fit-sum-${g.project_id}`;
                return (
                  <Box
                    component="li"
                    key={g.project_id}
                    sx={{
                      border: `1px solid ${WS_COLOR.border}`,
                      borderRadius: `${WS_RADIUS.card}px`,
                      backgroundColor: WS_COLOR.surface,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pr: 4,
                        backgroundColor: WS_COLOR.surfaceSubtle,
                        borderRadius: `${WS_RADIUS.card}px`,
                      }}
                    >
                      <Checkbox
                        checked={all}
                        indeterminate={some}
                        disabled={!analysed}
                        onChange={(e) =>
                          setSelected((s) => {
                            const n = new Set(s);
                            g.opps.forEach((o) =>
                              e.target.checked
                                ? n.add(o.opportunity_id)
                                : n.delete(o.opportunity_id),
                            );
                            return n;
                          })
                        }
                        inputProps={{ 'aria-label': `Select all of ${g.project_id}` }}
                      />
                      <Box
                        component="button"
                        type="button"
                        aria-expanded={open}
                        aria-controls={bodyId}
                        onClick={() => toggleOpen(g.project_id)}
                        sx={{
                          all: 'unset',
                          boxSizing: 'border-box',
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          minHeight: 48,
                          cursor: 'pointer',
                          '&:focus-visible': {
                            outline: `2px solid ${WS_COLOR.primary}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Icon icon={open ? icons.ChevronDown : icons.ChevronRight} size={16} />
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {g.project_id}
                        </Typography>
                        {g.description ? (
                          <Typography
                            component="span"
                            variant="body2"
                            noWrap
                            sx={{ color: WS_COLOR.textMuted, minWidth: 0 }}
                          >
                            — {g.description}
                          </Typography>
                        ) : null}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 'auto', flex: 'none', color: WS_COLOR.textMuted }}
                        >
                          {g.opps.length} initiative{g.opps.length === 1 ? '' : 's'}
                          {analysed ? ` · ${analysed} analyzed` : ''}
                        </Typography>
                      </Box>
                    </Box>

                    {open ? (
                      <Box id={bodyId} sx={{ overflowX: 'auto', ...PROSE_TABLE_SX }}>
                        <table aria-label={`Initiatives in ${g.project_id}`}>
                          <thead>
                            <tr>
                              <th style={{ width: 48 }}>
                                <span style={VISUALLY_HIDDEN}>Select</span>
                              </th>
                              <th style={{ width: '22%' }}>Initiative</th>
                              <th style={{ width: '26%' }}>Best fit</th>
                              <th>Why</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.opps.map((o) => (
                              <tr key={o.opportunity_id}>
                                <td>
                                  <Checkbox
                                    checked={selected.has(o.opportunity_id)}
                                    disabled={!o.analysis}
                                    onChange={() => toggle(o.opportunity_id)}
                                    inputProps={{ 'aria-label': `Select ${o.opportunity_id}` }}
                                  />
                                </td>
                                <td>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {o.opportunity_id}
                                  </Typography>
                                  {o.description ? (
                                    <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                                      {o.description}
                                    </Typography>
                                  ) : null}
                                </td>
                                <td>
                                  {o.bestFit ? (
                                    <>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          flexWrap: 'wrap',
                                          gap: 2,
                                        }}
                                      >
                                        <Typography component="span" sx={{ fontWeight: 600 }}>
                                          {o.bestFit}
                                        </Typography>
                                        {o.assigned ? (
                                          <StatusChip tone="success" label="ASSIGNED" />
                                        ) : null}
                                      </Box>
                                      {o.overallScore != null ||
                                      o.fitScore != null ||
                                      o.readinessScore != null ? (
                                        <Typography variant="caption" component="div">
                                          {[
                                            o.overallScore != null
                                              ? `Overall ${o.overallScore}`
                                              : null,
                                            o.fitScore != null ? `Fit ${o.fitScore}` : null,
                                            o.readinessScore != null
                                              ? `Ready ${o.readinessScore}`
                                              : null,
                                          ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                        </Typography>
                                      ) : null}
                                      <Box sx={{ mt: 1.5 }}>
                                        <Button
                                          size="small"
                                          variant="tonal"
                                          disabled={!o.assigned && o.bestFitPlatformId === null}
                                          onClick={() =>
                                            assign(o, o.assigned ? null : o.bestFitPlatformId)
                                          }
                                        >
                                          {o.assigned ? 'Unassign' : 'Assign'}
                                        </Button>
                                      </Box>
                                    </>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                                      Not analyzed
                                    </Typography>
                                  )}
                                </td>
                                <td>
                                  {o.explanation ? (
                                    <>
                                      {preview(o.explanation)}{' '}
                                      {o.analysis ? (
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => setPanel(o)}
                                          aria-label={`More: full justification for ${o.opportunity_id}`}
                                        >
                                          more
                                        </Button>
                                      ) : null}
                                    </>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                                      —
                                    </Typography>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </PageFrame>

      <Sheet
        open={!!panel}
        width={560}
        title={panel ? `${panel.opportunity_id} — ${panel.bestFit ?? ''}` : 'Justification'}
        onClose={() => setPanel(null)}
      >
        {panel?.analysis ? <Analysis a={panel.analysis} /> : null}
      </Sheet>

      <ConfirmDialog
        open={!!asking}
        title="Why is it changing?"
        confirmLabel="Continue"
        onConfirm={submitWhy}
        onClose={() => setAsking(null)}
      >
        Changing or clearing the assigned platform requires a justification.
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          inputProps={{ 'aria-label': 'Justification' }}
          sx={{ mt: 3 }}
        />
      </ConfirmDialog>
    </>
  );
}

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** The full six-section analysis, in the source's order and wording (as in PlatformFitSummary). */
function Analysis({ a }: { a: AirFitAnalysis }) {
  const cat = (title: string, arr?: { category: string; points: string[] }[]) =>
    Array.isArray(arr) && arr.length ? (
      <>
        <Typography variant="h3" component="h3" sx={{ mt: 4 }}>
          {title}
        </Typography>
        {arr.map((g, i) => (
          <Box key={i}>
            <Typography sx={{ fontWeight: 600, mt: 2 }}>{g.category}</Typography>
            <Box component="ul" sx={{ mt: 0.5, pl: 5 }}>
              {(g.points ?? []).map((p, j) => (
                <li key={j}>{p}</li>
              ))}
            </Box>
          </Box>
        ))}
      </>
    ) : null;

  const table = (label: string, head: string[], rows: string[][]) => (
    <Box sx={{ overflowX: 'auto', mt: 2, ...PROSE_TABLE_SX }}>
      <table aria-label={label}>
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );

  return (
    <>
      {a.executive_summary ? (
        <>
          <Typography variant="h3" component="h3">
            Executive summary
          </Typography>
          <Typography>{a.executive_summary}</Typography>
        </>
      ) : null}

      {a.business_drivers?.length ? (
        <>
          <Typography variant="h3" component="h3" sx={{ mt: 4 }}>
            Business drivers
          </Typography>
          {table(
            'Business drivers',
            ['Requirement', 'Importance', 'Assessment'],
            a.business_drivers.map((d) => [d.requirement, d.importance, d.assessment]),
          )}
        </>
      ) : null}

      {a.measurable ? (
        <>
          <Typography variant="h3" component="h3" sx={{ mt: 4 }}>
            Measurable justification
          </Typography>
          {a.measurable.rows?.length
            ? table(
                'Measurable justification',
                ['Area', 'Priority', 'Score', 'Justification'],
                a.measurable.rows.map((r) => [r.area, r.importance, r.score, r.justification]),
              )
            : null}
          {a.measurable.narrative ? <Typography>{a.measurable.narrative}</Typography> : null}
        </>
      ) : null}

      {cat('Strengths', a.strengths)}
      {cat('Limitations', a.limitations)}

      {a.risks?.length ? (
        <>
          <Typography variant="h3" component="h3" sx={{ mt: 4 }}>
            Business risks
          </Typography>
          {table(
            'Business risks',
            ['Risk', 'Impact', 'Mitigation'],
            a.risks.map((r) => [r.risk, r.impact, r.mitigation]),
          )}
        </>
      ) : null}

      {a.adoption_considerations ? (
        <>
          <Typography variant="h3" component="h3" sx={{ mt: 4 }}>
            Adoption considerations
          </Typography>
          <Typography>{a.adoption_considerations}</Typography>
        </>
      ) : null}
    </>
  );
}
